import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ClientContractStatus } from '@application/common/enums/client-contract-status';
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Container, Typography } from '@mui/material';
import { PageHeader } from '../../../../components/common/header/PageHeader';
import { SearchField } from '../../../../components/common/inputs/SearchField';
import { UserHourTable } from './table/UserHours';
import { useAppSelector } from '@application/store/useStore';
import { HoursStatus } from '@application/common/enums/hours-status';
import { adminHoursApi } from '@infrastructure/admin-hours-api';
import { ActivityIndicator, SegmentedControl } from '@ui/components/common/micro-interactions';
const assignmentLabel = (row) => ({ ...row, allClientNames: row.assignmentStatus === ClientContractStatus.Expired ? `${row.allClientNames} (Expired)` : row.allClientNames });
export const HoursRegistration = () => {
    const actor = useAppSelector(state => state.auth.currentUser);
    const [tab, setTab] = useState('1');
    const [search, setSearch] = useState('');
    const [attempt, setAttempt] = useState(0);
    const key = `${actor?.id || ''}:${actor?.role || ''}:${tab}:${attempt}`;
    const [result, setResult] = useState(null);
    useEffect(() => {
        const controller = new AbortController();
        setResult(null);
        if (!actor?.id || !['Admin', 'SuperAdmin'].includes(actor.role))
            return () => controller.abort();
        const load = async () => {
            try {
                if (tab === '4') {
                    const response = await adminHoursApi.inProgress(actor.id, controller.signal);
                    if (!Array.isArray(response.data))
                        throw new Error('Invalid hours response');
                    if (!controller.signal.aborted)
                        setResult({ key, rows: response.data.map(assignmentLabel) });
                }
                else {
                    const status = tab === '2' ? HoursStatus.Approved : tab === '3' ? HoursStatus.Rejected : HoursStatus.Pending;
                    const response = await adminHoursApi.list(status, actor.id, controller.signal);
                    if (!Array.isArray(response.data?.timeSheetWithClientInfo))
                        throw new Error('Invalid hours response');
                    if (!controller.signal.aborted)
                        setResult({ key, rows: response.data.timeSheetWithClientInfo.map(assignmentLabel), counts: response.data.timeSheetCount });
                }
            }
            catch {
                if (!controller.signal.aborted)
                    setResult({ key, error: 'Unable to load hours. Try again.' });
            }
        };
        void load();
        return () => controller.abort();
    }, [key, tab, actor?.id, actor?.role]);
    const current = result?.key === key ? result : null;
    const count = (n) => typeof n === 'number' ? ` (${n})` : '';
    const options = [
        { value: '4', label: 'In progress' },
        { value: '1', label: `To process${count(current?.counts?.toProcessCount)}` },
        { value: '2', label: `Processed${count(current?.counts?.processedCount)}` },
        { value: '3', label: `Declined${count(current?.counts?.rejectedCount)}` }
    ];
    if (!actor?.id || !['Admin', 'SuperAdmin'].includes(actor.role))
        return _jsx(Alert, { severity: 'error', children: "Sign in as an administrator to view hours." });
    return _jsxs(_Fragment, { children: [_jsx(PageHeader, { title: 'Hours' }), _jsxs(Container, { maxWidth: 'xl', children: [_jsx(Box, { mb: 3, sx: { overflowX: 'auto', pb: 1 }, children: _jsx(SegmentedControl, { options: options, value: tab, onChange: setTab, ariaLabel: 'Hours status' }) }), _jsx(Typography, { variant: 'body2', color: 'text.secondary', mb: 2, children: tab === '4' ? 'Saved hours that have not yet been submitted. Unsaved changes are not visible here.' : 'View registered hours by professional, assignment and month. Status counts show periods, not hours.' }), _jsx(SearchField, { onSearchChange: setSearch, searchQuery: search, mb: 3, bgColor: 'white', fullWidth: true, placeholder: 'Search professionals or clients', placeholderColor: '#666' }), !current && _jsx(Box, { py: 6, children: _jsx(ActivityIndicator, { label: 'Loading hours\u2026', announce: true }) }), current?.error && _jsx(Alert, { severity: 'error', action: _jsx(Button, { color: 'inherit', onClick: () => setAttempt(x => x + 1), children: "Retry" }), children: current.error }), current?.rows && _jsx(UserHourTable, { tab: Number(tab), timeSheetData: current.rows, searchQuery: search, onSelectionChange: () => { }, onActionComplete: () => setAttempt(x => x + 1), fetchTimeSheetData: () => setAttempt(x => x + 1) }, `${actor.id}:${actor.role}:${tab}`)] })] });
};
